import { KayttajanTiedot } from '@lemon/core-firebase/model/kayttaja'
import {
  Lasku,
  LaskuBase,
  LaskuReskontra,
  LaskunAsiakas,
  LaskunAsiakastyyppi,
  LaskunLahetystapa,
  LaskunLahetystyyppi,
  LaskunSahkoinenOsoite,
  LaskunTuote,
  LaskunTyypit,
  UUDEN_LASKUN_AVAIN,
} from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService, ReskontraService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { BatchManipulator, FirestoreProvider, FirestoreWriteBatch, JuurilaskuJaKasiteltava, LaskuTallennusService } from '@lemon/core-firebase/service/lasku/lasku-tallennus.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { LaskuIndeksoija } from '@lemon/core-firebase/service/lasku/lasku.indeksoija'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { TranslationService } from '@lemon/core-firebase/service/translation.service'
import { LocalDate, LocalMonth, TuettuKieli } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { TimestampProvider } from '@lemon/core/service/timestamp-provider.interface'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'
import { Asiakas, AsiakkaalleLemonatoristaLahetettyLasku } from '../model/asiakas'
import { LaskunAlvMaaritysFinder } from '../service/alv-lemonator-base.service'

export class AlvLaskunLahetysService {
  public static lahettavanAsiakkaanAsiakasId = 1598
  public static lahettavanAsiakkaanAsiakasAvain = 'o5AlyootOS13jv3I0dyH'
  private lahettavanAsiakkaanLahettavanKayttajanAvain = 'd8egQwuMihaEh1oSAErRCIHSkdo2' // Vero Karhu -käyttäjä tällä asiakkaalla

  private _laskuTallennusService: LaskuTallennusService

  constructor(
    private _translationService: TranslationService,
    private _dateService: DateService,
    private _firestoreProvider: FirestoreProvider,
    private _timestampService: TimestampProvider,
    private _currencyService: CurrencyService,
    private _viitenumeroService: ViitenumeroService,
    private _laskuKopioija: LaskuKopioija,
    private _stringService: StringService,
    private _laskuSharedService: LaskuSharedService,
    private _laskuIndeksoija: LaskuIndeksoija,
    private _laskuUriService: LaskuUriService,
    private _reskontraService: ReskontraService,
    private _alvLemonatorService: LaskunAlvMaaritysFinder,
  ) {
    this._laskuTallennusService = new LaskuTallennusService(
      this._laskuSharedService,
      this._laskuKopioija,
      this._laskuIndeksoija,
      this._currencyService,
      this._dateService,
      this._laskuUriService,
      this._translationService,
      this._timestampService,
      this._reskontraService,
      this._firestoreProvider,
      this._viitenumeroService,
      this._stringService,
    )
  }

  public async annaTallennettavaLasku(asiakasJolleLahetetaan: Asiakas, erapvm: LocalDate, summa: number, viitenumero: string, siirraErapaiva: boolean): Promise<Lasku> {
    // Tee lasku
    const lasku: Lasku = await this._laskuKopioija.annaUusiLasku()

    lasku.avain = UUDEN_LASKUN_AVAIN
    lasku.lahetystyyppi = LaskunLahetystyyppi.KERTA
    lasku.asiakas = this.lemonatorAsiakasLaskunAsiakkaaksi(asiakasJolleLahetetaan)

    // lasku.avain
    // lasku.date = this.timestampProvider.now()
    lasku.kieli = asiakasJolleLahetetaan.laskunKieli || 'fi'

    lasku.viitenumero = viitenumero

    // Päivämäärät
    const pvm = this._timestampService.now()
    const localPvm = this._dateService.timestampToLocalDate(pvm)

    if (siirraErapaiva) {
      // Jotkin pankit, kuten OP, eivät ota vastaan sähköisiä laskuja jos eräpäivä on liian lähellä nykyhetkeä.
      const nytPlus5 = this._dateService.lisaaPaiviaPaikallinen(this._dateService.currentLocalDate(), 5)
      if (this._dateService.compareLocalDates(erapvm, '<', nytPlus5)) {
        erapvm = nytPlus5
      }
    }

    lasku.date = pvm

    lasku.toimituspvm = pvm
    lasku.toimituspvml = localPvm
    lasku.toimitusp = this._dateService.localDateToNumber(localPvm)
    lasku.pvm = pvm
    lasku.pvml = localPvm
    lasku.p = this._dateService.localDateToNumber(localPvm)
    lasku.erapvm = this._timestampService.localDateToTimestamp(erapvm)
    lasku.erapvml = erapvm
    lasku.erap = this._dateService.localDateToNumber(erapvm)

    // Lisää tuote
    const tuote = await this.annaLaskunTuote(lasku.asiakas, summa, lasku.kieli)
    lasku.tuotteet = []
    lasku.tuotteet.push(tuote)
    return lasku
  }

  public async lahetaAlvLasku(
    asiakasJolleLahetetaan: Asiakas,
    sahkoinenLaskutusosoite: LaskunSahkoinenOsoite,
    erapvm: LocalDate,
    kuukausi: LocalMonth,
    summa: number,
    viitenumero: string,
    lahetystapa: 'vie-toisen-hyvaksyntajonoon' | 'laheta-heti-sahkoisesti',
    siirraErapaiva: 'siirra-erapaiva' | 'erapaivaa-ei-siirreta',
    lahetystiedonAvain?: string,
  ): Promise<JuurilaskuJaKasiteltava> {
    const lasku = await this.annaTallennettavaLasku(asiakasJolleLahetetaan, erapvm, summa, viitenumero, siirraErapaiva !== 'erapaivaa-ei-siirreta')

    // Tämä metodi luo uuden laskun (katso annaTallennettavaLasku -metodi), joten reskontrakin on pakosti tyhjä.
    const reskontra: LaskuReskontra[] = []

    const nyt = this._timestampService.now()
    const manipulator: BatchManipulator = {
      manipulate: (firestoreProvider: FirestoreProvider, batch: FirestoreWriteBatch, jl: Lasku, ka: LaskuBase) => {
        // Lisää jatkuvan laskun lähetystieto
        const tunniste = '___alv___' + kuukausi.year + '___' + kuukausi.month
        const lahetystieto: AsiakkaalleLemonatoristaLahetettyLasku = {
          avain: lahetystiedonAvain ?? firestoreProvider.annaUusiAvain(),
          asiakasAvain: asiakasJolleLahetetaan.avain,
          asiakasId: asiakasJolleLahetetaan.asiakasId + '',
          laskuAvain: lasku.avain,
          luoja: 'lemonator_system',
          luotu: nyt,
          paivitetty: nyt,
          paivittaja: 'lemonator_system',
          poistettu: false,
          tunniste: tunniste,
        }

        // console.log('Kirjoitetaan:', 'customers/' + asiakasJolleLahetetaan.avain + '/customer-sent-vat-invoice/' + lahetystieto.avain)
        const doc = firestoreProvider.annaDoc('customers/' + asiakasJolleLahetetaan.avain + '/customer-sent-vat-invoice/' + lahetystieto.avain)
        batch.set(doc, lahetystieto)
      },
    }

    const kayttajanTiedot: KayttajanTiedot = {
      asiakasId: AlvLaskunLahetysService.lahettavanAsiakkaanAsiakasId + '',
      asiakasAvain: AlvLaskunLahetysService.lahettavanAsiakkaanAsiakasAvain,
      uid: this.lahettavanAsiakkaanLahettavanKayttajanAvain,
    }

    if (lahetystapa === 'vie-toisen-hyvaksyntajonoon') {
      return this._laskuTallennusService.merkitseLaskuLahetetyksiTulostamallaToisenHyvaksyntajonoon(
        kayttajanTiedot,
        sahkoinenLaskutusosoite,
        lasku,
        lasku,
        reskontra,
        manipulator,
        null,
        asiakasJolleLahetetaan.avain,
      )
    } else if (lahetystapa === 'laheta-heti-sahkoisesti') {
      return this._laskuTallennusService.merkitseLaskuLahetetyksiSahkoisesti(kayttajanTiedot, sahkoinenLaskutusosoite, lasku, lasku, reskontra, manipulator, null, asiakasJolleLahetetaan.avain)
    } else {
      throw new Error('Unkown lahetystapa ' + lahetystapa)
    }
  }

  private annaAlv(alvt: { prosentti: number; tunniste: string }[]): { prosentti: number; tunniste: string } {
    if (alvt.length === 1) {
      return alvt[0]
    }
    for (const alv of alvt) {
      if (alv.prosentti === 0) {
        return alv
      }
    }
    return alvt[0]
  }

  private async annaLaskunTuote(asiakas: LaskunAsiakas, kuukausihinta: number, kieli: TuettuKieli): Promise<LaskunTuote> {
    const laskunTyyppi = LaskunTyypit.annaLaskunTyyppi(asiakas.laskunTyyppi)
    const laskutyypinAlvt = await this._alvLemonatorService.annaLaskutyypinAlvt(laskunTyyppi, asiakas.maa)
    const valittuAlv = this.annaAlv(laskutyypinAlvt)

    return {
      ale: null,
      alv: valittuAlv,
      hinta: kuukausihinta,
      maara: 1,
      tuote: {
        alv: {},
        avain: 'kirjanpitoalv',
        date: this._timestampService.now(),
        hinta: kuukausihinta,
        nimi: kieli === 'en' ? 'VAT' : 'ALV',
      },
    }
  }

  private lemonatorAsiakasLaskunAsiakkaaksi(asiakas: Asiakas): LaskunAsiakas {
    return {
      avain: asiakas.avain,
      date: this._timestampService.now(),
      katuosoite: asiakas.katuosoite,
      laskunKieli: asiakas.laskunKieli || 'fi',
      laskunTyyppi: LaskunTyypit.TAVALLINEN.avain,
      laskunValuutta: 'EUR',
      laskunVastaanottajat: asiakas.laskunVastaanottajat || [],
      maa: asiakas.maa,
      nimi: asiakas.nimi,
      postinro: asiakas.postinro,
      postitmp: asiakas.postitmp,
      ytunnus: asiakas.ytunnus,
      asiakastyyppi: LaskunAsiakastyyppi.YRITYS, // Kaikki Lemonatorin asiakkaat ovat yrityksiä
      sahkoinenosoite: asiakas.sahkoinenLaskutusosoite,
      viimeisinLaskuLahetetty: LaskunLahetystapa.SAHKOINEN,
    }
  }
}
